import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

export default function ModulePasswordsPage() {
  const qc = useQueryClient();
  const [passwords, setPasswords] = useState({});
  const [showPass, setShowPass]   = useState({});
  const [editing, setEditing]     = useState(null);

  const { data, isLoading } = useQuery({
    queryKey:['module-passwords'],
    queryFn:()=>api.get('/module-passwords').then(r=>r.data),
  });

  const updateMut = useMutation({
    mutationFn: ({module_name, is_enabled, password})=>api.put('/module-passwords/'+module_name,{is_enabled,password}),
    onSuccess:()=>{ qc.invalidateQueries(['module-passwords']); setEditing(null); toast.success('Updated!'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });

  const mods = data?.data || [];

  const handleSave = (mod) => {
    const pwd = passwords[mod.module_name];
    updateMut.mutate({ module_name:mod.module_name, is_enabled:mod.is_enabled, password:pwd });
    setPasswords(p=>({...p,[mod.module_name]:''}));
  };

  const handleToggle = (mod) => {
    updateMut.mutate({ module_name:mod.module_name, is_enabled:!mod.is_enabled });
  };

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Modules</div><div className="stat-val">{mods.length}</div></div>
        <div className="card-sm"><div className="stat-label">Protected</div><div className="stat-val" style={{color:'var(--green)'}}>{mods.filter(m=>m.is_enabled).length}</div></div>
        <div className="card-sm"><div className="stat-label">Unprotected</div><div className="stat-val" style={{color:'var(--muted)'}}>{mods.filter(m=>!m.is_enabled).length}</div></div>
      </div>

      <div className="card">
        <div className="form-section-title" style={{marginBottom:16}}>Module Access Control</div>
        <div style={{fontSize:12,color:'var(--muted)',marginBottom:20,padding:'10px 14px',background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)'}}>
          ⚠️ Enable password protection for sensitive modules. Users will need to enter the password each session to access the module.
        </div>
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <div style={{display:'flex',flexDirection:'column',gap:0}}>
            {mods.map((mod,idx)=>(
              <div key={mod.module_name} style={{
                display:'flex',alignItems:'center',gap:16,padding:'16px',
                borderBottom:idx<mods.length-1?'1px solid var(--border)':'none',
                background:mod.is_enabled?'#f0fdf7':'#fff'
              }}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:14}}>{mod.module_label}</div>
                  <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>
                    {mod.is_enabled
                      ? mod.has_password ? '🔒 Password protected' : '⚠️ Enabled but no password set'
                      : '🔓 No protection'}
                  </div>
                </div>

                {/* Toggle */}
                <div
                  onClick={()=>handleToggle(mod)}
                  style={{
                    width:48,height:26,borderRadius:13,cursor:'pointer',transition:'.3s',flexShrink:0,
                    background:mod.is_enabled?'var(--green)':'#e0e0e0',position:'relative'
                  }}
                >
                  <div style={{
                    position:'absolute',top:3,width:20,height:20,borderRadius:'50%',background:'#fff',
                    transition:'.3s',left:mod.is_enabled?'25px':'3px',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
                  }}/>
                </div>

                {/* Password input */}
                {mod.is_enabled && (
                  editing===mod.module_name ? (
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <div style={{position:'relative'}}>
                        <input
                          className="form-input"
                          type={showPass[mod.module_name]?'text':'password'}
                          value={passwords[mod.module_name]||''}
                          onChange={e=>setPasswords(p=>({...p,[mod.module_name]:e.target.value}))}
                          placeholder="Set new password..."
                          style={{width:200,paddingRight:50}}
                        />
                        <button type="button" onClick={()=>setShowPass(p=>({...p,[mod.module_name]:!p[mod.module_name]}))}
                          style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:11}}>
                          {showPass[mod.module_name]?'Hide':'Show'}
                        </button>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={()=>handleSave(mod)}>Save</button>
                      <button className="btn btn-outline btn-sm" onClick={()=>setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn btn-outline btn-sm" onClick={()=>setEditing(mod.module_name)}>
                      {mod.has_password?'Change Password':'Set Password'}
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
